import React from 'react';
import { 
  DocumentTextIcon, 
  LinkIcon, 
  BookOpenIcon 
} from '@heroicons/react/24/outline';
import Logo from '../components/common/Logo';

/**
 * Centralized source icon configuration and utility
 */

// Icon mappings with URL patterns and corresponding icons
export const SOURCE_ICON_MAPPINGS = [
  {
    patterns: ['notion.so', 'notion.site'],
    type: 'image',
    src: 'https://auth.openorg.fyi/storage/v1/object/public/web-assets/notion_icon.png',
    alt: 'Notion',
    title: 'Notion page',
    fallback: <DocumentTextIcon className="w-4 h-4" />
  },
  {
    patterns: ['openverse.fyi', 'openverse.org'],
    type: 'svg',
    component: (className = 'w-4 h-4', colors = {}) => (
      <svg 
        className={className} 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        title="Openverse resource"
      >
        <circle 
          cx="12" 
          cy="12" 
          r="8" 
          stroke="currentColor" 
          strokeWidth="2" 
          className={colors.primary || "text-indigo-500"}
        />
        <circle 
          cx="12" 
          cy="12" 
          r="3" 
          fill="currentColor" 
          className={colors.secondary || "text-indigo-400"}
        />
        <path 
          d="M12 2C13.1819 2 14.3522 2.23279 15.4442 2.68508C16.5361 3.13738 17.5282 3.80031 18.364 4.63604C19.1997 5.47177 19.8626 6.46392 20.3149 7.55585C20.7672 8.64778 21 9.8181 21 11" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          className={colors.accent || "text-purple-400"}
        >
          <animateTransform 
            attributeName="transform" 
            attributeType="XML" 
            type="rotate" 
            from="0 12 12" 
            to="360 12 12" 
            dur="20s" 
            repeatCount="indefinite"
          />
        </path>
        <circle 
          cx="17" 
          cy="7" 
          r="1" 
          fill="currentColor" 
          className={colors.dot || "text-indigo-300"}
        >
          <animate 
            attributeName="opacity" 
            values="0.5;1;0.5" 
            dur="2s" 
            repeatCount="indefinite"
          />
        </circle>
        <circle 
          cx="7" 
          cy="17" 
          r="1" 
          fill="currentColor" 
          className={colors.dot || "text-indigo-300"}
        >
          <animate 
            attributeName="opacity" 
            values="1;0.5;1" 
            dur="2s" 
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    ),
    fallback: <LinkIcon className="w-4 h-4" />
  },
  {
    patterns: ['github.com', 'github.io'],
    type: 'image',
    src: 'https://auth.openorg.fyi/storage/v1/object/public/web-assets/github_icon.png',
    alt: 'GitHub',
    title: 'GitHub repository',
    fallback: <LinkIcon className="w-4 h-4" />
  },
  {
    patterns: ['docs.', 'documentation.', 'gitbook.'],
    type: 'image',
    src: 'https://auth.openorg.fyi/storage/v1/object/public/web-assets/docs_icon.png',
    alt: 'Documentation',
    title: 'Documentation site',
    fallback: <DocumentTextIcon className="w-4 h-4" />
  },
  {
    patterns: ['newsletter', 'substack.com', 'mailchimp.com', 'beehiiv.com', 'convertkit.com'],
    type: 'svg',
    component: (className = 'w-4 h-4', colors = {}) => (
      <svg 
        className={className} 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        title="Newsletter"
      >
        <rect 
          x="3" 
          y="4" 
          width="18" 
          height="14" 
          rx="2" 
          stroke="currentColor" 
          strokeWidth="2" 
          className={colors.primary || "text-yellow-500"}
        />
        <path 
          d="M3 7l8.5 5.5a1 1 0 0 0 1 0L21 7" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className={colors.secondary || "text-yellow-400"}
        />
        <circle 
          cx="7" 
          cy="11" 
          r="1" 
          fill="currentColor" 
          className={colors.accent || "text-yellow-300"}
        />
        <circle 
          cx="17" 
          cy="13" 
          r="1" 
          fill="currentColor" 
          className={colors.accent || "text-yellow-300"}
        />
      </svg>
    ),
    fallback: <DocumentTextIcon className="w-4 h-4" />
  },
  {
    patterns: ['open_org_resource'],
    type: 'component',
    component: (className = 'w-4 h-4', colors = {}) => (
      <div className={`relative ${className} flex items-center justify-center`}>
        <Logo minimal={true} />
      </div>
    ),
    fallback: <LinkIcon className="w-4 h-4" />
  }
];

/**
 * Get the appropriate icon for a source based on its type and URL
 * @param {Object} source - Source object with type, url properties
 * @param {Object} options - Styling options
 * @param {string} options.className - CSS classes for the icon
 * @param {Object} options.colors - Color overrides for SVG icons
 * @param {boolean} options.muted - Whether to use muted styling
 * @returns {JSX.Element} - Icon component
 */
export const getSourceIcon = (source, options = {}) => {
  const { 
    className = 'w-4 h-4', 
    colors = {}, 
    muted = false 
  } = options;

  // Check for playbooks first (internal content)
  if (source.type === 'playbook' || (source.url && source.url.includes('/playbooks/'))) {
    return (
      <BookOpenIcon 
        className={`${className} ${muted ? 'text-gray-400' : 'text-primary'}`} 
        title="Playbook" 
      />
    );
  }

  // Check for newsletter type
  if (source.type === 'newsletter' || (source.metadata && source.metadata.category === 'newsletter')) {
    const newsletterMapping = SOURCE_ICON_MAPPINGS.find(mapping => 
      mapping.patterns.includes('newsletter')
    );
    
    if (newsletterMapping && newsletterMapping.type === 'svg') {
      const svgColors = muted ? {
        primary: 'text-gray-400',
        secondary: 'text-gray-500',
        accent: 'text-gray-500'
      } : colors;
      
      return (
        <div className={`relative ${className}`}>
          {newsletterMapping.component(className, svgColors)}
        </div>
      );
    }
  }

  // Check for Open Org Resource type
  if (source.type === 'open_org_resource' || (source.metadata && source.metadata.category === 'open_org_resource')) {
    const openOrgMapping = SOURCE_ICON_MAPPINGS.find(mapping => 
      mapping.patterns.includes('open_org_resource')
    );
    
    if (openOrgMapping && openOrgMapping.type === 'component') {
      return (
        <div className={`relative ${className} ${muted ? 'opacity-60' : ''}`}>
          {openOrgMapping.component(className, colors)}
        </div>
      );
    }
  }

  if (!source.url) {
    return <LinkIcon className={`${className} ${muted ? 'text-gray-400' : ''}`} />;
  }

  const url = source.url.toLowerCase();

  // Find matching icon mapping
  for (const mapping of SOURCE_ICON_MAPPINGS) {
    const matches = mapping.patterns.some(pattern => url.includes(pattern));
    
    if (matches) {
      if (mapping.type === 'image') {
        return (
          <div className={`relative ${className}`}>
            <img 
              src={mapping.src}
              alt={mapping.alt}
              title={mapping.title}
              className={`${className} object-contain`}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className={`${muted ? 'text-gray-400' : ''} hidden`}>
              {mapping.fallback}
            </div>
          </div>
        );
      } else if (mapping.type === 'svg') {
        // For muted contexts, override colors to be more subtle
        const svgColors = muted ? {
          primary: 'text-gray-400',
          secondary: 'text-gray-500',
          accent: 'text-gray-400',
          dot: 'text-gray-500'
        } : colors;
        
        return (
          <div className={`relative ${className}`}>
            {mapping.component(className, svgColors)}
          </div>
        );
      } else if (mapping.type === 'component') {
        return (
          <div className={`relative ${className} ${muted ? 'opacity-60' : ''}`}>
            {mapping.component(className, colors)}
          </div>
        );
      }
    }
  }

  // Default fallback icon
  return <LinkIcon className={`${className} ${muted ? 'text-gray-400' : ''}`} />;
};

/**
 * Add a new icon mapping (useful for runtime configuration)
 * @param {Object} mapping - Icon mapping configuration
 */
export const addIconMapping = (mapping) => {
  SOURCE_ICON_MAPPINGS.unshift(mapping); // Add to beginning for priority
};

/**
 * Get all configured icon mappings
 * @returns {Array} - Array of icon mappings
 */
export const getIconMappings = () => {
  return [...SOURCE_ICON_MAPPINGS];
}; 